/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Flex } from 'antd';
import * as R from 'ramda';

import DetailSpinner from '@totem/components/common/DetailSpinner';
import { EMPTY_ID } from '@totem/components/common/reference/ReferenceSelector';
import TenantMembershipContainer from '@totem/components/tenants/context/TenantMembershipContainer';
import { mainMenuPlacement } from '@totem/components/ui_components/constants';
import {
  getAccountPayloadSync,
  getToken,
  getTokenExpirationDate,
  setToken,
} from '@totem/components/userContext/accountUtilities';
import { TokenRefreshResponse } from '@totem/components/userContext/auth/types';
import OrganizationContainer from '@totem/components/userContext/organizationContext/OrganizationContainer';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { LoginProfile, UserProfile } from '@totem/types/user';
import api from '@totem/utilities/api';
import authUtilities from '@totem/utilities/authUtilities';
import { isNotNull, isNull } from '@totem/utilities/common';
import {
  LOGIN_PROFILE_ENDPOINT,
  TOKEN_REFRESH_ENDPOINT,
} from '@totem/utilities/endpoints';
import logger from '@totem/utilities/logger';
import { setPostLoginRedirect } from '@totem/utilities/loginRedirect';

type Props = {
  exact?: boolean;
  path?: string;
  component: any;
  roleSet: string[];
};

const TenantAuthorizedRoute = ({ component: Component, roleSet }: Props) => {
  const [refreshData, setRefreshData] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [doRefreshToken, setDoRefreshToken] = useState<boolean>(false);
  const [isTokenRefreshSending, setIsTokenRefreshSending] =
    useState<boolean>(false);
  const [error, setError] = useState<string>(null);

  const navigate = useNavigate();
  const location = useLocation();

  const [userProfile, setUserProfile] = useState<UserProfile>(null);
  const [userToken, setUserToken] = useState<string | null>(null);
  const [tokenTTL, setTokenTTL] = useState<number>(30);
  const [tokenRefreshCount, setTokenRefreshCount] = useState<number>(0);

  const retrieveUserId = () => {
    if (!getToken()) {
      return 'LOGIN';
    }

    // TODO: Remove @ts-ignore : Token
    // @ts-ignore
    return getAccountPayloadSync().id;
  };

  const setApiToken = (token: string | null, source: string) => {
    setUserToken(token); // Context

    setToken(token, `TenantAuthorizedRoute.setApiToken.${source}`); // Cookie

    setTokenRefreshCount((prevState) => prevState + 1);
  };

  const handleRefreshToken = () => {
    refreshToken();
  };

  const checkTokenBelowHalfTTL = (tokenExpiration: Date) => {
    if (tokenTTL > 0 && !isTokenRefreshSending && !doRefreshToken) {
      const now = new Date();
      const minutes =
        Math.abs(tokenExpiration.getTime() - now.getTime()) / 60000;
      if (minutes < tokenTTL / 2) {
        logger.debug(
          `TOKEN REFRESH: Minutes Until Token Expiration: ${minutes} / TTL: ${
            tokenTTL
          }`,
        );

        handleRefreshToken();
      }
    }
  };

  // Expired token: go to login, and come back to this page afterwards (see AuthCallback)
  const redirectToLogin = () => {
    setPostLoginRedirect(
      `${location.pathname}${location.search}${location.hash}`,
    );

    navigate('/login');
  };

  const getApiToken = () => {
    const now = new Date();
    if (isNotNull(userToken) && userToken !== '') {
      const expiration = getTokenExpirationDate(userToken);

      if (isNotNull(expiration)) {
        if (expiration.valueOf() < now.valueOf()) {
          redirectToLogin();
        }
        checkTokenBelowHalfTTL(expiration);
      }

      return userToken;
    }
    const tokenToReturn = getToken();
    const cookieTokenExpiration = getTokenExpirationDate(tokenToReturn);

    if (isNotNull(cookieTokenExpiration)) {
      if (cookieTokenExpiration.valueOf() < now.valueOf()) {
        redirectToLogin();
      }
      checkTokenBelowHalfTTL(cookieTokenExpiration);
    }

    setRefreshData(true);
    return tokenToReturn;
  };

  const refreshToken = useCallback(async () => {
    if (isTokenRefreshSending) {
      return;
    }
    setIsTokenRefreshSending(true);

    fetch(`${TOKEN_REFRESH_ENDPOINT}`, {
      method: 'GET',
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => {
        setIsTokenRefreshSending(false);
        if (res.status >= 400) {
          throw Error('Bad Response');
        }
        return res;
      })
      .then((res) => res.json())
      .then((result: TokenRefreshResponse) => {
        if (result.token !== '') {
          setApiToken(result.token, 'AuthorizedRoute.refreshToken');
        }
      });
  }, [getApiToken]);

  const getSoftApiToken = () => {
    if (isNotNull(userToken) && userToken !== '') {
      return userToken;
    }
    const tokenToReturn = getToken();
    return tokenToReturn;
  };

  const setProfileAndToken = (
    profile: UserProfile,
    newToken: string,
    source: string,
  ) => {
    if (newToken !== '') {
      setApiToken(
        newToken,
        `TenantAuthorizedRoute.setProfileAndToken.${source}`,
      );

      setUserProfile(profile);

      api.setAuthToken();
    }
  };

  const onCompleted = (user: UserProfile) => {
    if (isNull(user) || isNull(user.organizations)) {
      navigate('/login');
      return;
    }

    api.setAuthToken();

    setUserProfile(
      // sort user organizations by name
      R.over(R.lensPath(['organizations']), R.sortBy(R.prop('name')))(user),
    );

    const organizations = !user.organizations
      ? []
      : user.organizations.filter((org) => org.isActive);

    if (!user.isEmailVerified) {
      navigate('/unverified-email');
    } else if (R.isEmpty(organizations)) {
      logger.debug(JSON.stringify(user));

      navigate('/login');
    } else if (!user.id) {
      navigate('/login');
    } else if (!user.organization) {
      navigate('/switch-organization');
    } else if (!authUtilities.minimumRequiredRole(userProfile, roleSet)) {
      const endpoint = authUtilities.isTenantUser(userProfile)
        ? '/tenant-dashboard'
        : authUtilities.isVendorUser(userProfile)
          ? '/vendor-dashboard'
          : '/dashboard';

      navigate(endpoint);
    }
  };

  const userId = retrieveUserId();

  // useEffect #1
  useEffect(() => {
    if (userId === 'LOGIN') {
      const nextPath = `${location.pathname}${location.search}${location.hash}`;
      setPostLoginRedirect(nextPath);
    }
  }, [userId, location.pathname, location.search, location.hash]);

  // useEffect#2
  useEffect(() => {
    if (refreshData) {
      setRefreshData(false);

      fetch(`${LOGIN_PROFILE_ENDPOINT}/${userId}/${EMPTY_ID}`, {
        method: 'GET',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      })
        .then((res) => {
          if (res.status >= 400) {
            setError(res.statusText);
          }
          return res.json();
        })
        .then((result: LoginProfile) => {
          setApiToken(result.token, 'TenantAuthorizedRoute.useEffect#2');

          onCompleted(result.profile);
        })
        .then(() => {
          setIsLoading(false);
        });
    }
  }, [refreshData, getApiToken]);

  useEffect(() => {
    if (
      userProfile?.id &&
      !authUtilities.minimumRequiredRole(userProfile, roleSet)
    ) {
      const endpoint = authUtilities.isTenantUser(userProfile)
        ? '/tenant-dashboard'
        : authUtilities.isVendorUser(userProfile)
          ? '/vendor-dashboard'
          : '/dashboard';

      navigate(endpoint);
    }
  }, []);

  useEffect(() => {
    if (typeof error !== 'undefined') {
      if (error) {
        navigate('/login');
      }
    }
  }, [error]);

  // Use Effect #5
  useEffect(() => {
    if (doRefreshToken) {
      setDoRefreshToken(false);

      if (isTokenRefreshSending) {
        return;
      }
      setIsTokenRefreshSending(true);

      fetch(`${TOKEN_REFRESH_ENDPOINT}`, {
        method: 'GET',
        headers: new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        }),
      })
        .then((res) => {
          setIsTokenRefreshSending(false);
          if (res.status >= 400) {
            throw Error('Bad Response');
          }
          return res;
        })
        .then((res) => res.json())
        .then((result: TokenRefreshResponse) => {
          if (result.token !== '') {
            setApiToken(result.token, 'AuthorizedRoute.refreshToken');
          }
        });
    }
  }, [doRefreshToken]);

  if (userId === 'LOGIN') {
    return <Navigate to="/login" replace />;
  }

  if (!userProfile?.id) {
    return (
      <Flex justify={'center'} align={'center'} style={{ height: '100vh' }}>
        <DetailSpinner size={96} />
      </Flex>
    );
  }

  return (
    <UserProfileContext.Provider
      value={{
        loading: isLoading,
        refetch: () => setRefreshData(true),
        refreshToken: handleRefreshToken,
        userProfile,
        setProfile: setUserProfile,
        setProfileAndToken,
        showTooltips: false,
        setShowTooltips: () => {},
        setApiToken,
        getApiToken,
        getSoftApiToken,
        setTokenTTL,
        tokenTTL,
        tokenRefreshCount,
        mainMenuLocation: mainMenuPlacement,
        preferredTheme: 'light',
      }}
    >
      <OrganizationContainer>
        <TenantMembershipContainer>
          <div style={{ height: '100%' }}>
            <Component />
          </div>
        </TenantMembershipContainer>
      </OrganizationContainer>
    </UserProfileContext.Provider>
  );
};

export default TenantAuthorizedRoute;
